import { createContext, useContext } from 'react';

/**
 * Re-reads the ORIGINAL submission of a chat whose reply could not be confirmed. The owner (app.tsx) remounts the
 * pane, which takes the existing mount-time recovery path (the retained pending-turn descriptor, a read-only
 * GET /api/chat/recover/...). It never posts again.
 */
export const ChatCheckAgainContext = createContext<{
  ownerId: string;
  checkAgain: () => void;
} | null>(null);
export const useChatCheckAgain = () => useContext(ChatCheckAgainContext);

/**
 * The unknown outcome outlives the pane that saw it. The remount "Check again" causes starts from server history,
 * which has no memory of the dead request, and the existing recovery may legitimately end with nothing (an old 204 or
 * an exhausted lookup clears its descriptor and resumes null). So the notice is kept per signed-in owner and chat, with the original
 * user message id and how many answers existed, until an answer arrives or the person sends something new.
 */
/** `assistantCount` is null until the pane that shows the notice has counted the answers existing at that moment. */
export type UnconfirmedTurn = { messageId: string; assistantCount: number | null };
const keyFor = (ownerId: string, threadId: string) =>
  'unconfirmed-turn:' + JSON.stringify([ownerId, threadId]);
const listeners = new Set<() => void>();
const memory = new Map<string, string>();

function read(key: string): string | null {
  try {
    return window.sessionStorage.getItem(key) ?? memory.get(key) ?? null;
  } catch {
    return memory.get(key) ?? null;
  }
}

export function markUnconfirmed(ownerId: string, threadId: string, turn: UnconfirmedTurn) {
  const key = keyFor(ownerId, threadId);
  const value = JSON.stringify(turn);
  if (read(key) === value) return;
  memory.set(key, value);
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    /* The in-memory copy still serves this tab session. */
  }
  for (const listener of listeners) listener();
}

export function clearUnconfirmed(ownerId: string, threadId: string) {
  const key = keyFor(ownerId, threadId);
  if (read(key) === null) return;
  memory.delete(key);
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    /* Nothing stored to remove. */
  }
  for (const listener of listeners) listener();
}

export function readUnconfirmed(
  ownerId: string | undefined,
  threadId: string | undefined,
): UnconfirmedTurn | null {
  const raw = ownerId && threadId ? read(keyFor(ownerId, threadId)) : null;
  if (!raw) return null;
  try {
    const value = JSON.parse(raw);
    return typeof value?.messageId === 'string' &&
      (value.assistantCount === null || Number.isInteger(value.assistantCount))
      ? { messageId: value.messageId, assistantCount: value.assistantCount }
      : null;
  } catch {
    return null;
  }
}

export function subscribeUnconfirmed(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
